import { Editor } from "@tiptap/react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BoardAvatar from "./board-avatar";
import "./mention-suggest.scss";
import { DirectoryMember, getMembers } from "./people-directory";

// Shared @-mention autocomplete for the TipTap editors (comments + description). One source for the
// detection, keyboard navigation, and the portaled popup — so both editors behave identically and
// the popup is styled (and stays opaque) in one place.

// The active mention: the typed query, the document range of "@query" (so a pick replaces it), and
// the caret's viewport position (so the popup anchors under it).
export interface MentionState {
  query: string;
  from: number;
  to: number;
  left: number;
  top: number;
}

// Locate an in-progress @mention at the caret: an "@" that starts the line or follows whitespace,
// with no whitespace between it and the caret. Over ProseMirror positions so the pick can replace the
// exact range and the popup can anchor to it.
export function findMention(editor: Editor): MentionState | null {
  const { state } = editor;
  if (!state.selection.empty) return null;
  const caret: number = state.selection.from;
  const start: number = state.doc.resolve(caret).start();
  const textBefore: string = state.doc.textBetween(start, caret, "\n", "\n");
  const at: number = textBefore.lastIndexOf("@");
  if (at === -1) return null;
  const charBefore: string = at > 0 ? textBefore[at - 1]! : " ";
  if (at !== 0 && !/\s/.test(charBefore)) return null;
  const between: string = textBefore.slice(at + 1);
  if (/\s/.test(between)) return null;
  const fromPos: number = start + at;
  const coords = editor.view.coordsAtPos(fromPos);
  return { query: between, from: fromPos, to: caret, left: coords.left, top: coords.bottom };
}

// Mentions are name-based, but BoardAvatar resolves photo/initials from a user id. Map a candidate
// display name back to its directory member id (undefined when the name isn't a current member).
function memberIdForName(name: string): string | undefined {
  const match: DirectoryMember | undefined = getMembers().find((member: DirectoryMember) => member.name === name);
  return match?.id;
}

export interface MentionSuggest {
  // Recompute the active mention from the editor — call from onUpdate AND onSelectionUpdate.
  sync: (editor: Editor) => void;
  // Handle a keydown while the popup is open; returns true when it consumed the key (caller stops).
  // Stable across renders (reads live refs), so it's safe inside the editor's creation-time closure.
  handleKeyDown: (editor: Editor, event: KeyboardEvent) => boolean;
  // The portaled popup (or null) — render it in the editor's JSX.
  popup: (editor: Editor | null) => React.ReactNode;
}

export function useMentionSuggest(candidates: string[]): MentionSuggest {
  const [mention, setMention] = useState<MentionState | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const matches: string[] = mention
    ? candidates.filter((name: string) => name.toLowerCase().includes(mention.query.toLowerCase())).slice(0, 6)
    : [];

  // Live refs so the editor's (creation-time) key handler reads current state.
  const mentionRef = useRef<MentionState | null>(mention);
  const matchesRef = useRef<string[]>(matches);
  const activeIndexRef = useRef<number>(activeIndex);
  mentionRef.current = mention;
  matchesRef.current = matches;
  activeIndexRef.current = activeIndex;

  const popupRef = useRef<HTMLDivElement | null>(null);

  const pickMention = (editor: Editor, name: string): void => {
    const current: MentionState | null = mentionRef.current;
    if (!current) return;
    editor.chain().focus().insertContentAt({ from: current.from, to: current.to }, `@${name} `).run();
    setMention(null);
  };

  // Stable keydown handler via a ref the editor closure always reads through.
  const handleKeyDownRef = useRef<(editor: Editor, event: KeyboardEvent) => boolean>(() => false);
  handleKeyDownRef.current = (editor: Editor, event: KeyboardEvent): boolean => {
    if (mentionRef.current === null || matchesRef.current.length === 0) return false;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev: number) => Math.min(matchesRef.current.length - 1, prev + 1));
      return true;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev: number) => Math.max(0, prev - 1));
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      pickMention(editor, matchesRef.current[activeIndexRef.current]!);
      return true;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setMention(null);
      return true;
    }
    return false;
  };

  // Close on an outside click (the popup is portaled to <body>, so test the ref directly).
  useEffect(() => {
    if (!mention) return;
    const onDown = (event: MouseEvent): void => {
      if (!popupRef.current?.contains(event.target as Node)) setMention(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [mention]);

  // Keep the highlighted match in range as the query narrows.
  useEffect(() => {
    if (activeIndex > matches.length - 1) setActiveIndex(0);
  }, [matches.length, activeIndex]);

  const popup = (editor: Editor | null): React.ReactNode => {
    if (!mention || matches.length === 0) return null;
    // Portal to <body>: an ancestor's `will-change: transform` (the issue panel) would otherwise be
    // the containing block for this fixed popup and offset it by the panel's box.
    return createPortal(
      <div
        ref={popupRef}
        className="kuika-mention-suggest"
        role="listbox"
        style={{ position: "fixed", left: mention.left, top: mention.top + 4, zIndex: 200 }}
      >
        {matches.map((name: string, index: number) => (
          <button
            key={name}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            className={`kuika-mention-suggest__item${index === activeIndex ? " kuika-mention-suggest__item--active" : ""}`}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseDown={(event: React.MouseEvent) => {
              event.preventDefault();
              if (editor) pickMention(editor, name);
            }}
          >
            <BoardAvatar userId={memberIdForName(name)} className="kuika-mention-suggest__avatar" ariaHidden />
            {name}
          </button>
        ))}
      </div>,
      document.body
    );
  };

  return {
    sync: (editor: Editor): void => setMention(findMention(editor)),
    handleKeyDown: (editor: Editor, event: KeyboardEvent): boolean => handleKeyDownRef.current(editor, event),
    popup
  };
}
